
import React, { useMemo } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface MathRendererProps {
  text: string;
  inline?: boolean;
}

/**
 * Regular expressions to identify LaTeX math expressions
 * - Block math: Enclosed in $$ ... $$ or \[ ... \]
 * - Inline math: Enclosed in $ ... $ or \( ... \)
 */
const BLOCK_MATH_REGEX = /(\$\$(.*?)\$\$|\\\[(.*?)\\\])/gs;
const INLINE_MATH_REGEX = /(\$(.*?)\$|\\\((.*?)\\\))/g;

/**
 * Physics-specific notation patterns that should be automatically wrapped in math delimiters
 * if they aren't already within math delimiters
 */
const PHYSICS_PATTERNS = [
  // s-t and v-t graph patterns
  /\bs\s*-\s*t\b/g,       // s-t
  /\bv\s*-\s*t\b/g,       // v-t
  /\ba\s*-\s*t\b/g,       // a-t
  /\bd\/dt\b/g,           // d/dt
  /\b(d[xyzstuv])\/d([xyzt])\b/g, // dx/dt, dy/dt, etc.
  /\b\Delta[xyzstuv]\b/g, // Δx, Δs, etc.
  /\b\Delta[xyzstuv]\/\Delta[xyzt]\b/g, // Δx/Δt, etc.
];

/**
 * Renders text that may contain LaTeX math expressions
 */
const MathRenderer: React.FC<MathRendererProps> = ({ text, inline = false }) => {
  // If text doesn't contain any math expressions, check for physics patterns
  if (!text || (!text.includes('$') && !text.includes('\\('))) {
    // Look for physics patterns that should be rendered as math
    for (const pattern of PHYSICS_PATTERNS) {
      if (pattern.test(text)) {
        // Replace physics patterns with inline math delimiters
        text = text.replace(pattern, match => `$${match}$`);
      }
    }
    
    // If still no math delimiters after replacement, return plain text
    if (!text.includes('$') && !text.includes('\\(')) {
      return <>{text}</>;
    }
  }

  const renderContent = useMemo(() => {
    // For completely inline math content, just render it directly
    if (inline) {
      try {
        return <InlineMath math={text} />;
      } catch (error) {
        console.error('Error rendering inline math:', error);
        return text;
      }
    }
    
    // For mixed content, we need to parse and render parts differently
    let lastIndex = 0;
    const result: JSX.Element[] = [];
    let match;
    
    // First try to find block math expressions
    const blockMatches = Array.from(text.matchAll(BLOCK_MATH_REGEX));
    if (blockMatches.length > 0) {
      let currentIndex = 0;
      
      blockMatches.forEach((blockMatch, idx) => {
        // Add text before this block math
        if (blockMatch.index! > currentIndex) {
          const textBefore = text.slice(currentIndex, blockMatch.index);
          result.push(
            <span key={`text-${idx}`}>
              {renderInlineMath(textBefore)}
            </span>
          );
        }
        
        // Extract the math expression without the delimiters
        const mathContent = blockMatch[2] || blockMatch[3] || '';
        try {
          result.push(
            <div key={`block-math-${idx}`} className="my-2 text-center">
              <BlockMath math={mathContent} />
            </div>
          );
        } catch (error) {
          console.error('Error rendering block math:', error, mathContent);
          result.push(<span key={`block-error-${idx}`}>{blockMatch[0]}</span>);
        }
        
        currentIndex = blockMatch.index! + blockMatch[0].length;
      });
      
      // Add any remaining text
      if (currentIndex < text.length) {
        result.push(
          <span key="text-end">
            {renderInlineMath(text.slice(currentIndex))}
          </span>
        );
      }
      
      return <>{result}</>;
    }
    
    // If no block math, check for inline math
    return renderInlineMath(text);
  }, [text, inline]);
  
  return <>{renderContent}</>;
};

// Helper function to render text with inline math expressions
function renderInlineMath(text: string): JSX.Element[] {
  // Look for physics patterns that should be rendered as math
  for (const pattern of PHYSICS_PATTERNS) {
    // Only replace outside of existing math delimiters
    let inMath = false;
    let result = '';
    let lastIndex = 0;
    
    // Simple parser to avoid replacing inside existing math delimiters
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '$' || (text[i] === '\\' && text[i+1] === '(')) {
        inMath = !inMath;
      }
    }
    
    if (!inMath) {
      text = text.replace(pattern, match => {
        // Don't add delimiters if already within delimiters
        if (match.startsWith('$') || match.startsWith('\\(')) return match;
        return `$${match}$`;
      });
    }
  }
  
  if (!text.includes('$') && !text.includes('\\(')) {
    return [<span key="plain-text">{text}</span>];
  }
  
  const parts: JSX.Element[] = [];
  let lastIndex = 0;
  let match;
  
  // Find all inline math expressions
  while ((match = INLINE_MATH_REGEX.exec(text)) !== null) {
    // Add text before this inline math
    if (match.index > lastIndex) {
      parts.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    
    // Extract the math expression without the delimiters
    const mathContent = match[2] || match[3] || '';
    try {
      parts.push(
        <span key={`inline-math-${match.index}`} className="inline-block">
          <InlineMath math={mathContent} />
        </span>
      );
    } catch (error) {
      console.error('Error rendering inline math:', error, mathContent);
      parts.push(<span key={`inline-error-${match.index}`}>{match[0]}</span>);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={`text-end-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }
  
  return parts;
}

export default MathRenderer;
